/**
 * Resolve the brand accent color for a flow based on its type.
 *
 * Each flow type has its own design accent in the wizard (FlowTypeStep). The
 * thank-you page used to hardcode emerald + charcoal for the icon and button;
 * this helper pulls the type-appropriate accent so the thank-you screen feels
 * like the same brand as the rest of the flow.
 *
 * Returns a hex color string. Caller still chooses how to apply it (background
 * tint, button bg, etc.) and should pair it with `contrastText()` when used
 * as a button background.
 */
export function getFlowAccent(flow: any | null | undefined, fallback = "#10b981"): string {
  if (!flow || typeof flow !== "object") return fallback;

  const type = (flow.flow_type as string | undefined) || "";
  switch (type) {
    case "referral":
      return flow.referral_color || fallback;
    case "newsletter":
      return flow.newsletter_color || fallback;
    case "pricing_table":
      return flow.pricing_table_design?.primaryColor || fallback;
    case "signup":
    case "waitlist":
      return (
        flow.waitlist_design?.primaryColor ||
        flow.waitlist_design?.bgColor ||
        fallback
      );
    // Payment-style flows ship with the yellow Pay button — match that.
    case "payment":
    case "subscription":
    case "preorder":
    case "discount":
    case "buy_button":
      return "#fbbf24";
    default:
      return fallback;
  }
}

/**
 * Wizard-form variant of {@link getFlowAccent}. Reads from FlowFormData
 * (camelCase fields) instead of the persisted DB row (snake_case).
 */
export function getFormAccent(flowData: any | null | undefined, fallback = "#10b981"): string {
  if (!flowData || typeof flowData !== "object") return fallback;

  const type = (flowData.flowType as string | undefined) || "";
  switch (type) {
    case "referral":
      return flowData.referralColor || fallback;
    case "newsletter":
      return flowData.newsletterColor || fallback;
    case "pricing_table":
      return flowData.pricingTableDesign?.primaryColor || fallback;
    case "signup":
    case "waitlist":
      return (
        flowData.waitlistDesign?.primaryColor ||
        flowData.waitlistDesign?.bgColor ||
        fallback
      );
    case "payment":
    case "subscription":
    case "preorder":
    case "discount":
    case "buy_button":
      return "#fbbf24";
    default:
      return fallback;
  }
}

/** Quick perceived-luminance check for picking foreground text color. */
export function isLightHex(hex: string): boolean {
  const c = (hex || "").replace("#", "");
  if (c.length !== 6) return false;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return r * 0.299 + g * 0.587 + b * 0.114 > 186;
}

/** Returns black or white text appropriate for the given background hex. */
export function contrastTextFor(hex: string): string {
  return isLightHex(hex) ? "#111827" : "#ffffff";
}
